<template>
  <v-menu
    min-width="550px"
    max-width="550px"
    v-model="opened"
    :close-on-content-click="false"
  >
    <template v-slot:activator="{ on }">
      <v-tooltip top>
        <template v-slot:activator="{ on: onTooltip }">
          <v-btn :small="small" icon v-on="{...on, ...onTooltip}" :disabled="readonly">
            <v-icon :small="small" :color="iconColor">mdi-text-box-plus-outline</v-icon>
          </v-btn>
        </template>
        <span>Cadastros adicionais do produto</span>
      </v-tooltip>
    </template>
    <v-card class="mx-auto">
      <v-card-text class="text--primary">
        <v-row dense>
          <v-col cols="12">
            <px-autocomplete-projeto
              :dense="dense"
              :class="`rounded-tag-small ${isProjetoRequired ? 'required' : ''}`"
              :error="$v.produto.projeto.$error"
              @blur="$v.produto.projeto.$touch()"
              :disabled="readonly || isProjetoReadonly"
              v-model="produto.projeto"/>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12">
            <px-autocomplete-centro-custo
              :dense="dense"
              :class="`rounded-tag-small ${isCentroCustoRequired ? 'required' : ''}`"
              :error="$v.produto.centroCusto.$error"
              @blur="$v.produto.centroCusto.$touch()"
              :disabled="readonly || isCentroCustoReadonly"
              v-model="produto.centroCusto"/>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" sm="6">
            <px-select-enum
              :dense="dense"
              label="Finalidade da compra"
              :items="finalidades"
              class="rounded-tag-small"
              :disabled="readonly"
              v-model="produto.finalidadeCompra"/>
          </v-col>
          <v-col cols="12" sm="6">
            <px-date-picker input-class="required rounded-tag-small"
                            :dense="dense"
                            :label="labelPrevisaoEntrega"
                            :disabled="readonly"
                            :error="$v.produto.dataPrevisaoEntrega.$error"
                            @blur="$v.produto.dataPrevisaoEntrega.$touch()"
                            v-model="produto.dataPrevisaoEntrega"/>
          </v-col>
        </v-row>
        <v-row dense v-if="showNotaEntrada">
          <v-col cols="12">
            <px-autocomplete-nota-entrada
              dense
              :class="`rounded-tag-small ${isTipoNotaEntradaRequired ? 'required' : ''}`"
              :error="$v.produto.tipoNotaEntrada.$error"
              @blur="$v.produto.tipoNotaEntrada.$touch()"
              :disabled="readonly"
              v-model="produto.tipoNotaEntrada"/>
          </v-col>
        </v-row>
        <template v-if="showCadastrosAdicionais">
          <px-campos-integracao :dense="dense"
                                :readonly="readonly || isCadastrosAdicionaisReadonly"
                                :form-data="cadastrosAdicionais"
                                :type="cadastrosAdicionaisType"
                                :single-line="singleLine"
                                @replicar-campo="onReplicarCampo"/>
        </template>
        <v-row dense>
          <v-col cols="12">
            <v-btn @click="onClickConcluir" color="primary"
                   class="rounded-tag-small normal-btn-text elevation-0">
              Concluir
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<script>
import { requiredIf, required } from 'vuelidate/lib/validators';
import {
  isFieldRequiredEntityItens,
  isFieldReadonlyEntityItens,
} from '@/modules/erp-campos/erp-campos-utils';
import validationMixin from '@/mixins/validation-mixin';
import { FinalidadeCompra } from 'px-business-components';
import { checkMenorDataValida } from '@/utils/date-utils';

export default {
  mixins: [validationMixin],
  props: {
    produto: {
      type: Object,
      required: true,
    },
    small: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    showCadastrosAdicionais: {
      type: Boolean,
      default: false,
    },
    showNotaEntrada: {
      type: Boolean,
      default: false,
    },
    cadastrosAdicionais: {
      type: Object,
    },
    cadastrosAdicionaisType: {
      type: String,
      default: 'compra-item',
    },
    camposObrigatorios: {
      type: Object,
      default: null,
    },
    labelPrevisaoEntrega: {
      type: String,
      default: 'Data de entrega',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    minDataPrevisaoEntrega: {
      type: [String, Date],
      default: () => new Date(),
    },
    isFromRequisicao: {
      type: Boolean,
      default: false,
    },
    camposReadonly: {
      type: Object,
      default: null,
    },
    singleLine: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      opened: false,
      finalidades: FinalidadeCompra.values,
    };
  },
  computed: {
    iconColor() {
      if ((this.$v.$invalid && this.$v.$dirty) || !this.produto) {
        return 'error';
      }
      if (this.hasField) {
        return 'primary';
      }
      return '';
    },
    isCentroCustoRequired() {
      return isFieldRequiredEntityItens('centroCusto', this.camposObrigatorios);
    },
    isTipoNotaEntradaRequired() {
      return isFieldRequiredEntityItens('tipoNotaEntrada', this.camposObrigatorios);
    },
    isProjetoRequired() {
      return isFieldRequiredEntityItens('projeto', this.camposObrigatorios);
    },
    isProjetoReadonly() {
      return isFieldReadonlyEntityItens('projeto', this.camposReadonly) && this.isFromRequisicao;
    },
    isCentroCustoReadonly() {
      return isFieldReadonlyEntityItens('centroCusto', this.camposReadonly) && this.isFromRequisicao;
    },
    isCadastrosAdicionaisReadonly() {
      return isFieldReadonlyEntityItens('cadastrosAdicionais', this.camposReadonly) && this.isFromRequisicao;
    },
    hasField() {
      return this.produto.centroCusto || this.produto.projeto
      || this.produto.finalidadeCompra || this.produto.dataPrevisaoEntrega;
    },
  },
  methods: {
    onClickConcluir() {
      this.opened = false;
    },
    onReplicarCampo(obj) {
      this.$emit('replicar-campo', obj);
    },
    validate() {
      return this.validateForm();
    },
  },
  validations: {
    produto: {
      centroCusto: {
        required: requiredIf(function () {
          return this.isCentroCustoRequired;
        }),
      },
      tipoNotaEntrada: {
        required: requiredIf(function () {
          return this.isTipoNotaEntradaRequired;
        }),
      },
      projeto: {
        required: requiredIf(function () {
          return this.isProjetoRequired;
        }),
      },
      dataPrevisaoEntrega: {
        required,
        customDataValidation(value) {
          if (this.produto.id) {
            return true;
          }
          return checkMenorDataValida(value, this.minDataPrevisaoEntrega);
        },
      },
    },
  },
};
</script>
